import moment, {Moment} from "moment/moment";

import DateConstants from "#app/helpers/DateConstants";

/**
 * Стандартная длина эвента в календаре
 */
const DEFAULT_CALENDAR_EVENT_LENGTH: number = 30;

export const DEFAULT_BACKEND_FORMAT_MASK: string = 'YYYY-MM-DD HH:mm:ss';
/**
 *
 * @param date
 */
export const dateStringBackendMask = (
    date: moment.Moment | Moment
): string | null | undefined => date && date.isValid() ? date.format(DEFAULT_BACKEND_FORMAT_MASK) : null;

/**
 * Список возможных ошибок, выбрасываемых при использовании хелперов
 */
const ERRORS_LIST = {
    error_start_more_than_end: `Ошибка при вычислении разницы в датах: дата начала не должна быть позднее даты окончания`,
    error_different_days: `Ошибка при вычислении разницы в датах: дата окончания и дата начала не могут быть назначены на разные дни, событие происходит в рамках дня`,
} as const;

/**
 * Входной тип переменных для moment, сделан дженериком, если нужно будет потом обрабатывать какие-то из типов специфически
 */
export type GenericMomentInput<T = string | Date> = T;

/**
 * Обработчик для вычисления разницы между двумя датами
 * @param dateStart
 * @param dateEnd
 */
export const getTimeDifference = (dateStart: GenericMomentInput, dateEnd: GenericMomentInput): number | null => {
    const momentDateStart: moment.Moment = moment(dateStart);
    const momentDateEnd: moment.Moment = moment(dateEnd);

    if (!momentDateEnd.isAfter(momentDateStart)) return null; // ставим полчаса, если падает в ошибку
    if (!momentDateStart.isSame(momentDateEnd, 'day')) return null;
    return momentDateEnd.diff(momentDateStart, 'minutes');
}

/**
 * Обработчик для подготовки
 * @param date {GenericMomentInput} инстанс даты нового события либо строка с такой датой
 * @param timeDiff {number|null}
 */
export const prepareDate = (
    date: GenericMomentInput,
    timeDiff: number | null = null
): { start_at_datetime: string; end_at_datetime: string } => {
    let momentDate: moment.Moment = moment(date).startOf('minute');

    if (momentDate.hour() === 0 && momentDate.minute() === 0) {
        momentDate = momentDate.hour(8).minute(0);
    }

    const momentDateEnd: moment.Moment = (timeDiff !== null)
        ? moment(momentDate).add(timeDiff, 'minutes')
        : momentDate.clone().add(DEFAULT_CALENDAR_EVENT_LENGTH, 'minutes');

    if (momentDate.hour() === 8 && momentDate.minute() === 0 && timeDiff === null) {
        momentDateEnd.hour(8).minute(30);
    }

    return {
        start_at_datetime: momentDate.format(DateConstants.dateTimeUserFormat),
        end_at_datetime: momentDateEnd.format(DateConstants.dateTimeUserFormat),
    };
};

export const setDateInMultiMonth = (
    date: GenericMomentInput<Moment>,
    timeDiff: number | null = null
): { start_at_datetime: string; end_at_datetime: string } => {
    let momentDate = moment(date);
    if (momentDate.hour() === 0 && momentDate.minute() === 0) {
        momentDate = momentDate.hour(8).minute(0);
    }

    const originalStartDate = moment(momentDate);
    const durationInMinutes = timeDiff !== null ? timeDiff : DEFAULT_CALENDAR_EVENT_LENGTH;
    let momentDateEnd = moment(momentDate).add(durationInMinutes, 'minutes');

    if (originalStartDate.isBefore(momentDate)) {
        const timeShift = momentDate.diff(originalStartDate, 'minutes');
        momentDateEnd = momentDateEnd.add(timeShift, 'minutes');
    }

    return {
        start_at_datetime: momentDate.format(DateConstants.dateTimeUserFormat),
        end_at_datetime: momentDateEnd.format(DateConstants.dateTimeUserFormat),
    };
};

/**
 * Обработчик для приведения дат к формату, который нужен для дальнейшей обработки календарем и корректной работы апи
 * @param dateToFormat {GenericMomentInput} форматируемая дата
 * @param formatString {string} строка с маской для форматирования
 * @param momentFormatMask {string|null|undefined} легаси-код: маска форматирования исходной даты, по умолчанию 'DD.MM.YYYY'
 */
export const formatDate = (
    dateToFormat: GenericMomentInput,
    formatString: string,
    momentFormatMask: string | null | undefined = null,
): string => moment(dateToFormat, momentFormatMask ?? 'DD.MM.YYYY').format(formatString);

/**
 *
 */
export const prepareDateNow = () => {
    let momentDate = moment();
    let momentDateAdded30Minutes: moment.Moment;

    if (momentDate.minutes() >= 30) {
        momentDate.minutes(30).seconds(0);
        momentDateAdded30Minutes = momentDate.clone().startOf('hour').add(1, 'hour');

        return {
            start_at_datetime: momentDate.format(DateConstants.dateTimeUserFormat),
            end_at_datetime: momentDateAdded30Minutes.format(DateConstants.dateTimeUserFormat),
        }
    } else {
        momentDate.startOf('hour').seconds(0);
        momentDateAdded30Minutes = momentDate.clone().startOf('hour').add(1, 'hour');

        return {
            start_at_datetime: momentDate.format(DateConstants.dateTimeUserFormat),
            end_at_datetime: momentDateAdded30Minutes.format(DateConstants.dateTimeUserFormat),
        }
    }
}

export const fixCalendarEndDate = (start: moment.Moment, end: moment.Moment): [moment.Moment, moment.Moment] => {
    const newStart = start.clone();
    const newEnd = end.clone();

    if (
        newStart.isSame(newEnd, 'month') &&
        newStart.isSame(newEnd, 'year')
    ) {
        const nextDayStart = newStart.clone().add(1, 'day').startOf('day');
        if (newEnd.isSame(nextDayStart)) {
            newEnd.set({
                year: newStart.year(), month: newStart.month(), date: newStart.date(),
                hour: 23, minute: 59, second: 59, millisecond: 0
            });
        }
    }

    return [newStart, newEnd];
};
